import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * AfriMarket-branded head meta tags. Moved out of public/index.html so that
 * react-helmet-async fully owns the document head — this lets PlyxSEO cleanly
 * override the head on /plyx routes without leaving duplicate / leaked
 * AfriMarket og:/twitter: tags behind that social-media scrapers would pick
 * up (the original bug: 'afrimarket.org is still on the preview').
 *
 * Mounted from App.js only when NOT on a Plyx route.
 */
const AfriMarketSEO = () => (
  <Helmet>
    <meta name="keywords" content="AfriMarket, AfriMarket.org, African diaspora marketplace, buy African products UK, African services USA, African marketplace Canada, Nigerian shop UK, Ghanaian food USA, Ankara, Adinkra, African crafts diaspora" />
    <meta name="author" content="AfriMarket Group" />

    <meta property="og:type" content="website" />
    <meta property="og:site_name" content="AfriMarket Group" />
    <meta property="og:title" content="AfriMarket Group — The Official African Diaspora Marketplace" />
    <meta property="og:description" content="AfriMarket Group — the official marketplace for the African diaspora. Authentic products and trusted local services across the UK, US and Canada — with a real human buyer guarantee." />
    <meta property="og:url" content="https://afrimarket.org/" />
    <meta property="og:image" content="https://afrimarket.org/og-image.png" />
    <meta property="og:image:width" content="1200" />
    <meta property="og:image:height" content="630" />
    <meta property="og:image:alt" content="AfriMarket Group — the official African diaspora marketplace" />
    <meta property="og:locale" content="en_GB" />

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content="AfriMarket Group — The Official African Diaspora Marketplace" />
    <meta name="twitter:description" content="AfriMarket Group — the official marketplace for the African diaspora across the UK, US and Canada. Buy authentic. Sell locally." />
    <meta name="twitter:image" content="https://afrimarket.org/og-image.png" />
    <meta name="twitter:site" content="@afrimarket" />

    <meta name="apple-mobile-web-app-title" content="AfriMarket Group" />
    <meta name="application-name" content="AfriMarket Group" />

    <link rel="manifest" href="/manifest.json" />
    <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
    <link rel="apple-touch-icon" href="/logomark.svg" />
    <link rel="mask-icon" href="/favicon.svg" color="#C05A3C" />

    {/* Organization structured data */}
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "AfriMarket Group",
      legalName: "AfriMarket Group Ltd",
      alternateName: ["AfriMarket", "AfriMarket.org", "AfriMarket Diaspora Marketplace"],
      identifier: { "@type": "PropertyValue", propertyID: "UK Companies House", value: "17282940" },
      foundingDate: "2026",
      address: { "@type": "PostalAddress", addressCountry: "GB" },
      url: "https://afrimarket.org",
      logo: "https://afrimarket.org/logomark.png",
      description: "The official marketplace for the African diaspora across the UK, US and Canada — distinct from any similarly-named site. Authentic products and trusted local services. Operated by AfriMarket Group Ltd (UK Companies House #17282940).",
      sameAs: [
        "https://www.instagram.com/afrimarket",
        "https://www.facebook.com/afrimarket",
        "https://www.tiktok.com/@afrimarket",
      ],
      contactPoint: { "@type": "ContactPoint", email: "admin@afrimarket.org", contactType: "customer support", availableLanguage: ["English"] },
    })}</script>
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: "AfriMarket Group",
      alternateName: ["AfriMarket", "AfriMarket.org", "AfriMarket — the Official African Diaspora Marketplace"],
      url: "https://afrimarket.org",
      potentialAction: {
        "@type": "SearchAction",
        target: { "@type": "EntryPoint", urlTemplate: "https://afrimarket.org/browse?q={search_term_string}" },
        "query-input": "required name=search_term_string",
      },
    })}</script>
  </Helmet>
);

export default AfriMarketSEO;
